import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BookmarkPlus, Bookmark, Trash2, Eye, ThumbsUp, MessageCircle, Clock, Youtube, Quote, Sparkles, Loader2, Trophy, Wand2, Copy, Check } from "lucide-react";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Checkbox } from "./components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatNumber = (num) => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
  return String(num ?? 0);
};

/** Compact "Save to swipe file" toggle button used inside CompetitorCard.
 *  Reads its own bookmarked state from the saved-set passed by the parent so
 *  the button correctly shows "Saved" on a re-render of competitor results. */
export function SwipeSaveButton({ video, savedSet, onSavedChange, index }) {
  const isSaved = savedSet?.has(video.video_id);
  const [busy, setBusy] = useState(false);

  // Disable until lazy-load brings in transcript+analysis (no point saving an empty hook)
  const ready = Boolean(video.video_id && video.video_url && (video.analysis || video.transcript_full_300_words));

  const toggle = async () => {
    if (!ready || busy) return;
    setBusy(true);
    try {
      if (isSaved) {
        await axios.delete(`${API}/swipe-file/${video.video_id}`);
        onSavedChange?.(video.video_id, false);
        toast.success("Removed from swipe file");
      } else {
        await axios.post(`${API}/swipe-file`, {
          video_id: video.video_id,
          title: video.title || "",
          channel_name: video.channel_name || "",
          thumbnail_url: video.thumbnail_url || "",
          video_url: video.video_url,
          view_count: video.view_count || 0,
          like_count: video.like_count || 0,
          comment_count: video.comment_count || 0,
          duration: video.duration || "",
          transcript: video.transcript_full_300_words || video.transcript_first_150_words || "",
          analysis: video.analysis || null,
        });
        onSavedChange?.(video.video_id, true);
        toast.success("Saved to swipe file");
      }
    } catch (e) {
      toast.error(isSaved ? "Couldn't remove" : "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      size="sm"
      onClick={toggle}
      disabled={!ready || busy}
      variant="outline"
      className={
        isSaved
          ? "bg-yellow-500/[0.08] border-yellow-500/30 text-yellow-300 hover:bg-yellow-500/[0.14] hover:text-yellow-200"
          : "bg-white/[0.03] border-white/15 text-zinc-200 hover:bg-white/[0.08] hover:text-white"
      }
      data-testid={`swipe-save-btn-${index}`}
      title={!ready ? "Wait for the hook analysis to finish" : isSaved ? "Remove from your swipe file" : "Save this hook to your swipe file"}
    >
      {busy ? (
        <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
      ) : isSaved ? (
        <Bookmark className="w-4 h-4 mr-1.5 fill-current" />
      ) : (
        <BookmarkPlus className="w-4 h-4 mr-1.5" />
      )}
      {isSaved ? "Saved" : "Save to swipe file"}
    </Button>
  );
}

/** Hook the SwipeFile screen + CompetitorCard share to track which competitor
 *  videos the current user has already saved. One fetch on mount, in-memory
 *  optimistic updates after that — no extra GET per save/unsave. */
export function useSwipeFile() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedSet, setSavedSet] = useState(new Set());

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/swipe-file`);
      setItems(data || []);
      setSavedSet(new Set((data || []).map((i) => i.video_id)));
    } catch (e) {
      // Silent — empty state on the SwipeFile page is informative enough
      setItems([]);
      setSavedSet(new Set());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const markSaved = (videoId, saved) => {
    setSavedSet((prev) => {
      const next = new Set(prev);
      if (saved) next.add(videoId);
      else next.delete(videoId);
      return next;
    });
    // List view will get the new row on its next refresh; for the optimistic
    // "Saved" pill on the competitor card the set update above is enough.
  };

  return { items, loading, savedSet, refresh, markSaved };
}

/** Full Swipe File page — the user's saved hooks, newest first. */
export function SwipeFileScreen({ onAnalyzeCompetitor }) {
  const { items, loading, refresh, markSaved } = useSwipeFile();
  const [deletingId, setDeletingId] = useState(null);

  const remove = async (videoId) => {
    setDeletingId(videoId);
    try {
      await axios.delete(`${API}/swipe-file/${videoId}`);
      markSaved(videoId, false);
      await refresh();
      toast.success("Removed from swipe file");
    } catch (e) {
      toast.error("Couldn't remove");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="py-20 text-center text-zinc-500" data-testid="swipe-file-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3 text-red-500" />
        Loading your swipe file…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="py-20 text-center" data-testid="swipe-file-empty">
        <Bookmark className="w-10 h-10 mx-auto mb-3 text-zinc-700" />
        <div className="text-zinc-300 text-base font-medium mb-1">No saved hooks yet</div>
        <div className="text-zinc-500 text-sm max-w-md mx-auto mb-5">
          Find a hook you love in Competitor Analysis and tap &ldquo;Save to swipe file&rdquo; to keep it here for when you script your next video.
        </div>
        <Button
          onClick={onAnalyzeCompetitor}
          className="bg-red-600 hover:bg-red-500 text-white border border-red-400/30 shadow-[0_0_18px_rgba(255,0,0,0.3)]"
          data-testid="swipe-file-go-to-competitors-btn"
        >
          <Trophy className="w-4 h-4 mr-1.5" />
          Find a hook to save
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-up" data-testid="swipe-file-list">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="text-sm text-zinc-500" data-testid="swipe-file-count">
          {items.length} saved hook{items.length === 1 ? "" : "s"}
        </div>
        <BlendHooksDialog items={items} />
      </div>
      {items.map((item, i) => (
        <SwipeFileCard key={item.id} item={item} index={i} onDelete={remove} deleting={deletingId === item.video_id} />
      ))}
    </div>
  );
}

/** "Blend with AI" dialog — sends the user's chosen saves + topic to Claude
 *  and renders 3 fresh hook variations the creator can copy straight into a
 *  script. Defaults to the 5 most-recent saves so the happy path is one click + a topic. */
function BlendHooksDialog({ items }) {
  const [open, setOpen] = useState(false);
  const [topic, setTopic] = useState("");
  // Default selection: the 5 newest saves (items is already sorted newest-first).
  const newestFive = items.slice(0, 5).map((i) => i.video_id);
  const [selected, setSelected] = useState(new Set(newestFive));
  const [hooks, setHooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);

  // Reset selection whenever the dialog opens — keeps it in sync with the
  // current swipe file (which may have grown/shrunk since last open).
  useEffect(() => {
    if (open) {
      setSelected(new Set(items.slice(0, 5).map((i) => i.video_id)));
      setHooks([]);
    }
    // eslint-disable-next-line
  }, [open]);

  const toggle = (videoId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(videoId)) next.delete(videoId);
      else next.add(videoId);
      return next;
    });
  };

  const generate = async () => {
    if (!topic.trim()) {
      toast.error("Enter your topic first");
      return;
    }
    if (selected.size === 0) {
      toast.error("Select at least one saved hook to blend from");
      return;
    }
    setLoading(true);
    setHooks([]);
    try {
      const { data } = await axios.post(`${API}/swipe-file/blend-hooks`, {
        topic: topic.trim(),
        video_ids: Array.from(selected),
      });
      setHooks(data.hooks || []);
    } catch (e) {
      // Suppress the toast on usage_cap_hit — the global axios interceptor in
      // UsageProvider pops a dedicated upgrade modal so a second red toast is
      // just noise. For every other failure show a plain-string message.
      const detail = e?.response?.data?.detail;
      const isCapHit = e?.response?.status === 429 && detail && typeof detail === "object" && detail.code === "usage_cap_hit";
      if (!isCapHit) {
        const msg = typeof detail === "string" ? detail : (detail?.message || "Failed to blend hooks");
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const copyHook = async (text, i) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(i);
      toast.success("Hook copied");
      setTimeout(() => setCopiedIdx(null), 2000);
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          className="bg-red-600 hover:bg-red-500 text-white border border-red-400/30 shadow-[0_0_18px_rgba(255,0,0,0.3)] hover:shadow-[0_0_24px_rgba(255,0,0,0.5)]"
          data-testid="blend-hooks-open-btn"
        >
          <Wand2 className="w-4 h-4 mr-1.5" />
          Blend with AI
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto bg-zinc-950 border border-white/10 text-zinc-100">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Wand2 className="w-5 h-5 text-red-500" />
            Blend your saved hooks
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Claude reads the frameworks + tactics from your selected saves and writes 3 fresh
            opening-hook variations for your own topic. No copy-paste — synthesised originals.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          <div>
            <Label htmlFor="blend-topic" className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
              Your video topic / niche
            </Label>
            <Input
              id="blend-topic"
              data-testid="blend-hooks-topic-input"
              placeholder="e.g. AI coding tools for solo founders"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !loading && generate()}
              className="bg-black/40 border-white/10 text-white mt-2"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
                Blend from ({selected.size} of {items.length})
              </Label>
              {items.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelected(selected.size === items.length ? new Set() : new Set(items.map((i) => i.video_id)))}
                  className="text-xs text-red-400 hover:text-red-300"
                  data-testid="blend-hooks-toggle-all"
                >
                  {selected.size === items.length ? "Clear all" : "Select all"}
                </button>
              )}
            </div>
            <div className="space-y-1.5 max-h-56 overflow-y-auto rounded-lg border border-white/[0.08] bg-black/30 p-2">
              {items.map((item, i) => {
                const isOn = selected.has(item.video_id);
                return (
                  <label
                    key={item.id}
                    htmlFor={`blend-pick-${i}`}
                    className={`flex items-center gap-3 px-2 py-1.5 rounded-md cursor-pointer transition-colors ${
                      isOn ? "bg-red-500/[0.08]" : "hover:bg-white/[0.04]"
                    }`}
                    data-testid={`blend-hooks-pick-${i}`}
                  >
                    <Checkbox
                      id={`blend-pick-${i}`}
                      checked={isOn}
                      onCheckedChange={() => toggle(item.video_id)}
                      className="border-white/20 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-200 truncate">{item.title}</div>
                      <div className="text-[11px] text-zinc-500 truncate">
                        {item.analysis?.hook_type || "no analysis"}
                        {item.channel_name && ` · ${item.channel_name}`}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <Button
            onClick={generate}
            disabled={loading || !topic.trim() || selected.size === 0}
            className="w-full bg-red-600 hover:bg-red-500 text-white border border-red-400/30 shadow-[0_0_18px_rgba(255,0,0,0.3)]"
            data-testid="blend-hooks-generate-btn"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Blending {selected.size} hook{selected.size === 1 ? "" : "s"}…</>
            ) : (
              <><Wand2 className="w-4 h-4 mr-2" /> Generate 3 hooks</>
            )}
          </Button>

          {hooks.length > 0 && (
            <div className="space-y-3" data-testid="blend-hooks-results">
              {hooks.map((h, i) => (
                <div
                  key={`${h.slice(0, 40)}-${i}`}
                  className="rounded-xl p-4 bg-red-500/[0.06] border border-red-500/15"
                  data-testid={`blend-hooks-result-${i}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <Badge className="bg-red-500/15 text-red-300 border border-red-500/25 hover:bg-red-500/15">
                      Variation {i + 1}
                    </Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyHook(h, i)}
                      className="h-8 text-zinc-400 hover:text-white hover:bg-white/5"
                      data-testid={`blend-hooks-copy-${i}`}
                    >
                      {copiedIdx === i ? (
                        <><Check className="w-4 h-4 mr-1" /> Copied</>
                      ) : (
                        <><Copy className="w-4 h-4 mr-1" /> Copy</>
                      )}
                    </Button>
                  </div>
                  <p className="text-sm text-zinc-200 leading-relaxed">{h}</p>
                  <p className="text-xs text-zinc-500 mt-2">{h.split(/\s+/).length} words</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SwipeFileCard({ item, index, onDelete, deleting }) {
  const analysis = item.analysis;
  return (
    <div
      data-testid={`swipe-file-card-${index}`}
      className="relative overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-xl p-6 hover:bg-white/[0.04] hover:border-white/[0.15] transition-all duration-300"
    >
      <div className="flex items-start gap-5">
        {item.thumbnail_url && (
          <div className="flex-shrink-0">
            <img
              src={item.thumbnail_url}
              alt={item.title}
              className="rounded-lg object-cover border border-white/10"
              style={{ width: 144, height: 81 }}
            />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white text-base mb-1 line-clamp-2" data-testid={`swipe-file-title-${index}`}>
            {item.title}
          </h3>
          {item.channel_name && (
            <div className="text-xs text-zinc-500 mb-2">{item.channel_name}</div>
          )}

          <div className="mb-3">
            <a
              href={item.video_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 font-medium text-sm transition-colors"
              data-testid={`swipe-file-watch-link-${index}`}
            >
              <Youtube className="w-4 h-4" />
              <span>Watch on YouTube</span>
            </a>
          </div>

          <div className="flex items-center flex-wrap gap-x-5 gap-y-1 mb-4 text-sm text-zinc-400">
            <div className="flex items-center gap-1.5"><Eye className="w-4 h-4 text-zinc-500" /><span><span className="text-zinc-200">{formatNumber(item.view_count)}</span> views</span></div>
            <div className="flex items-center gap-1.5"><ThumbsUp className="w-4 h-4 text-zinc-500" /><span><span className="text-zinc-200">{formatNumber(item.like_count)}</span> likes</span></div>
            <div className="flex items-center gap-1.5"><MessageCircle className="w-4 h-4 text-zinc-500" /><span><span className="text-zinc-200">{formatNumber(item.comment_count)}</span> comments</span></div>
            {item.duration && (
              <div className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-zinc-500" /><span>{item.duration}</span></div>
            )}
          </div>

          {analysis && (
            <div
              className="rounded-xl bg-red-500/[0.05] border border-red-500/15 p-4 mb-3"
              data-testid={`swipe-file-analysis-${index}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-red-400" />
                <span className="font-semibold text-white text-sm">AI Hook Analysis</span>
                {analysis.hook_type && (
                  <Badge className="bg-red-500/15 text-red-300 border border-red-500/25 hover:bg-red-500/15 ml-1">
                    {analysis.hook_type}
                  </Badge>
                )}
              </div>
              {analysis.one_sentence_summary && (
                <p className="text-sm text-zinc-300 mb-3 leading-relaxed">{analysis.one_sentence_summary}</p>
              )}
              {analysis.copywriting_frameworks?.length > 0 && (
                <div className="mb-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5 font-semibold">Frameworks</div>
                  <div className="flex flex-wrap gap-1.5">
                    {analysis.copywriting_frameworks.map((f, i) => (
                      <Badge key={`${f}-${i}`} className="bg-white/5 text-zinc-300 border border-white/10 hover:bg-white/5 font-normal">
                        {f}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {analysis.patterns?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5 font-semibold">Tactics spotted</div>
                  <ul className="space-y-1">
                    {analysis.patterns.map((p, i) => (
                      <li key={`${p}-${i}`} className="text-sm text-zinc-300 flex items-start gap-2">
                        <span className="text-red-400 mt-0.5">•</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {item.transcript && (
            <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Quote className="w-4 h-4 text-zinc-500" />
                <span className="font-medium text-zinc-300 text-sm">Opening Hook</span>
              </div>
              <p className="text-zinc-300 text-sm leading-relaxed italic" data-testid={`swipe-file-transcript-${index}`}>
                &ldquo;{item.transcript}&rdquo;
              </p>
            </div>
          )}
        </div>

        <div className="flex-shrink-0">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(item.video_id)}
            disabled={deleting}
            className="text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
            data-testid={`swipe-file-delete-btn-${index}`}
            title="Remove from swipe file"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
